import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { Svp, SvpEntity } from '../../../svp/models/svp.model';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { SchoolService } from '../../../schools/services/school-service/school-service';
import { RvpVersionService } from '../../../rvp-version/service/rvp-version-service/rvp-version-service';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';

@Injectable({
  providedIn: 'root',
})
export class SvpDetailService {
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly authService = inject(AuthService);
  private readonly schoolService = inject(SchoolService);
  private readonly rvpVersionService = inject(RvpVersionService);
  readonly #activeSvpId = signal<string | null>(null);
  readonly activeSvpId = this.#activeSvpId.asReadonly();

  readonly #svpResource = httpResource<SvpEntity>(() => {
    if (!this.authService.isAuthenticated()) return undefined;
    const svpId = this.#activeSvpId();
    if (!svpId) {
      return undefined;
    }
    return `${environment.apiUrl}/svps/${svpId}`;
  });

  readonly svp = computed<Svp | undefined>(() => {
    if (this.#svpResource.status() === 'error') {
      return undefined;
    }
    const entity = this.#svpResource.value();
    if (!entity) {
      return undefined;
    }
    const school = this.schoolService.schools().find((s) => s.id === entity.schoolId);
    const rvpVersion = this.rvpVersionService
      .rvpVersions()
      .find((r) => r.id === entity.rvpVersionId);
    if (!school || !rvpVersion) {
      return undefined;
    }
    return {
      ...entity,
      school,
      rvpVersion,
    };
  });

  readonly rvpVersion = computed(() => {
    return this.svp()?.rvpVersion.version;
  });

  readonly isLocked = computed(() => this.svp()?.locked ?? false);

  readonly isLoading = computed(() => this.#svpResource.isLoading());

  constructor() {
    effect(() => {
      if (this.#svpResource.status() === 'error') {
        this.apiErrors.notify(this.#svpResource.error(), 'SvpDetailService.loadSvp');
      }
    });
    // The open ŠVP decides which school the user is working in: a bookmark or a shared link can
    // open any school's ŠVP no matter what the header showed, and the header, the license state
    // and the home dashboard all follow the one selection rather than each judging by its own.
    effect(() => {
      const school = this.svp()?.school;
      if (school) {
        this.schoolService.syncSelectedSchool(school.id);
      }
    });
  }

  setActiveSvpId(id: string) {
    this.#activeSvpId.set(id);
  }

  /**
   * Left the ŠVP shell. The id has to go, not just the loaded ŠVP, so the resource stops fetching
   * and the next ŠVP opened starts from nothing rather than briefly showing the previous one.
   */
  clearActiveSvpId() {
    this.#activeSvpId.set(null);
  }

  reload(): void {
    this.#svpResource.reload();
  }
}
